import React, { useRef, useState } from 'react';
import TransferBackfillService from './TransferBackfillService';

// Turns a company-account bank slip (recorded by an admin while Beam links were down)
// into the Beam payment rows the company accounting documents read.
// Flow: slip date + amount → preview → confirm. Nothing is written until confirm.

const AMOUNT_TYPE_LABELS = {
    productPayment: 'ค่าสินค้า (เต็ม)',
    productPaymentFirstHalf: 'ค่าสินค้า 50% แรก',
    productPaymentSecondHalf: 'ค่าสินค้า 50% หลัง',
    transactionFeePaid: 'ค่าบริการ',
    extraJpFeesPaid: 'ค่าตรวจ',
    shipJpPaid: 'ค่าส่งญี่ปุ่น (เต็ม)',
    shipJpFirstHalf: 'ค่าส่งญี่ปุ่น 50% แรก',
    shipJpSecondHalf: 'ค่าส่งญี่ปุ่น 50% หลัง',
};

const baht = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const errorMessage = (err) => err?.response?.data?.message || err?.message || 'เกิดข้อผิดพลาด';

const MatchCard = ({ match, onConfirm, confirming }) => (
    <div className={`rounded-lg border-2 bg-white p-4 ${match.canConfirm ? 'border-emerald-300' : 'border-red-300'}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
                <div className="text-lg font-bold">
                    สลิป #{match.receiptId} · {baht(match.slipAmount)} บาท
                </div>
                <div className="text-sm text-gray-600">
                    เวลาโอน (กรุงเทพ) {match.paymentDateBangkok} · บัญชี {match.accountNo || '—'} ·
                    ลูกค้า {match.customer ? `${match.customer.smileName} (#${match.customer.id})` : '—'}
                </div>
            </div>
            <button
                type="button"
                disabled={!match.canConfirm || confirming}
                onClick={() => onConfirm(match)}
                className={`px-4 py-2 rounded font-semibold text-white ${match.canConfirm && !confirming ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-300 cursor-not-allowed'}`}
            >
                {confirming ? 'กำลังบันทึก…' : 'ยืนยันบันทึก'}
            </button>
        </div>

        {match.blocking.length > 0 && (
            <ul className="mt-3 rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 list-disc list-inside">
                {match.blocking.map((b) => <li key={b}>{b}</li>)}
            </ul>
        )}
        {match.warnings.length > 0 && (
            <ul className="mt-2 rounded border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 list-disc list-inside">
                {match.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
        )}

        <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="text-left text-gray-500 border-b">
                        <th className="py-1 pr-3">ออเดอร์</th>
                        <th className="py-1 pr-3">รายการ</th>
                        <th className="py-1 pr-3 text-right">ยอด (บาท)</th>
                    </tr>
                </thead>
                <tbody>
                    {match.rows.map((r) => (
                        <tr key={`${r.orderId}-${r.amountType}`} className="border-b last:border-0">
                            <td className="py-1 pr-3">
                                <a href={r.orderUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">#{r.orderId}</a>
                            </td>
                            <td className="py-1 pr-3">{AMOUNT_TYPE_LABELS[r.amountType] || r.amountType}</td>
                            <td className="py-1 pr-3 text-right">{baht(r.orderAmount)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="font-semibold">
                        <td className="pt-2 pr-3" colSpan={2}>รวมที่คำนวณ / ยอดสลิป</td>
                        <td className={`pt-2 pr-3 text-right ${Math.abs(match.diff) > 1 ? 'text-red-700' : ''}`}>
                            {baht(match.total)} / {baht(match.slipAmount)}
                            {match.diff !== 0 && ` (ต่าง ${baht(match.diff)})`}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
);

const TransferBackfill = () => {
    const [date, setDate] = useState('');
    const [amount, setAmount] = useState('');
    const [matches, setMatches] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirmingId, setConfirmingId] = useState(null);
    const [error, setError] = useState('');
    // Saved this session — a running log while working through the statement.
    const [done, setDone] = useState([]);
    const amountRef = useRef(null);

    const handleLookup = async (e) => {
        e.preventDefault();
        setError('');
        setMatches(null);
        if (!date || !(Number(amount) > 0)) {
            setError('กรอกวันที่และยอดเงิน');
            return;
        }
        setLoading(true);
        try {
            const res = await TransferBackfillService.lookup(date, Number(amount));
            setMatches(res.data.matches);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (match) => {
        const ok = window.confirm(
            `บันทึกสลิป #${match.receiptId} ยอด ${baht(match.slipAmount)} บาท ` +
            `(${match.rows.length} รายการ) เป็นการชำระเข้าบริษัท?`
        );
        if (!ok) return;
        setError('');
        setConfirmingId(match.receiptId);
        try {
            const res = await TransferBackfillService.confirm(match.receiptId);
            setDone((prev) => [{ ...match, beamPurchaseId: res.data.beamPurchaseId }, ...prev]);
            setMatches(null);
            setAmount('');
            amountRef.current?.focus();
        } catch (err) {
            // A 400 carries the re-checked preview — show why it was refused.
            const preview = err?.response?.data?.preview;
            if (preview) setMatches((prev) => prev.map((m) => (m.receiptId === preview.receiptId ? preview : m)));
            setError(errorMessage(err));
        } finally {
            setConfirmingId(null);
        }
    };

    return (
        <div className="p-4 max-w-5xl">
            <h1 className="text-2xl font-bold">บันทึกโอนตรงเข้าบัญชีบริษัท → เอกสารบัญชี</h1>
            <p className="mt-1 text-sm text-gray-600">
                สำหรับสลิปที่ลูกค้าโอนเข้าบัญชีบริษัท (SCB 429-190745-9) ช่วงที่ลิงก์ Beam ใช้ไม่ได้
                — กรอกวันที่ตามเวลาไทยและยอดจาก statement ระบบจะหาสลิปที่แอดมินแนบไว้ แล้วแสดงรายการก่อนบันทึก
            </p>

            <form onSubmit={handleLookup} className="mt-4 flex flex-wrap items-end gap-3">
                <label className="text-sm">
                    <div className="text-gray-600">วันที่โอน (เวลาไทย)</div>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border rounded px-2 py-1" />
                </label>
                <label className="text-sm">
                    <div className="text-gray-600">ยอดเงิน (บาท)</div>
                    <input
                        ref={amountRef}
                        type="number"
                        step="0.01"
                        min="0"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="border rounded px-2 py-1 w-40"
                    />
                </label>
                <button type="submit" disabled={loading} className="px-4 py-1.5 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:bg-gray-300">
                    {loading ? 'กำลังค้นหา…' : 'ค้นหา'}
                </button>
            </form>

            {error && <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

            {matches && matches.length === 0 && (
                <div className="mt-4 rounded border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                    ไม่พบสลิปวันที่ {date} ยอด {baht(amount)} บาท — แอดมินอาจยังไม่ได้แนบสลิป หรือวันที่/ยอดไม่ตรง
                </div>
            )}

            {matches && matches.length > 1 && (
                <div className="mt-4 text-sm font-semibold text-amber-700">
                    พบ {matches.length} สลิปที่วันที่และยอดตรงกัน — เลือกให้ถูกใบ (ดูเวลาโอนและลูกค้า)
                </div>
            )}

            <div className="mt-4 space-y-4">
                {(matches || []).map((m) => (
                    <MatchCard key={m.receiptId} match={m} onConfirm={handleConfirm} confirming={confirmingId === m.receiptId} />
                ))}
            </div>

            {done.length > 0 && (
                <div className="mt-8">
                    <h2 className="text-lg font-bold">บันทึกแล้วในรอบนี้ ({done.length})</h2>
                    <table className="mt-2 min-w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 border-b">
                                <th className="py-1 pr-3">เลขอ้างอิง</th>
                                <th className="py-1 pr-3">เวลาโอน</th>
                                <th className="py-1 pr-3">ลูกค้า</th>
                                <th className="py-1 pr-3 text-right">ยอด</th>
                            </tr>
                        </thead>
                        <tbody>
                            {done.map((d) => (
                                <tr key={d.receiptId} className="border-b last:border-0">
                                    <td className="py-1 pr-3 font-mono">{d.beamPurchaseId}</td>
                                    <td className="py-1 pr-3">{d.paymentDateBangkok}</td>
                                    <td className="py-1 pr-3">{d.customer?.smileName || '—'}</td>
                                    <td className="py-1 pr-3 text-right">{baht(d.slipAmount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TransferBackfill;
